import { Draggable } from '@hello-pangea/dnd';
import classNames from 'classnames';
import { useEffect } from 'react';
import { Popover } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import { useFramework } from '@/framework';
import { LayerType, type ILayerProps } from '@/version-types';
import { StageToolbar } from '../../../stage-toolbar';
import { HIERARCHY_EVENTS } from '../../constants';
import { type HierarchyMenuItem } from '../../types';
// import { getLayerTitle } from '../../utils';
import { Menu } from '../menu';
import { HierarchyThumbnail } from './thumbnail';
import './style.less';

type Props = React.ComponentProps<'div'> & {
    active: boolean;
    layer: ILayerProps;
    index: number;
    iconRenderVersion: number;
    ancestorLayerLocked?: boolean;
    menuOpen?: boolean;
    onClick: () => void;
    onOpenMenu?: () => void;
    onUpdate: () => void;
    onCloseMenu?: () => void;
    onHover?: (flag: boolean) => void;
};

export const Item: React.FC<Props> = ({
    className,
    style,
    active,
    layer,
    index,
    iconRenderVersion,
    ancestorLayerLocked = false,
    menuOpen = false,
    onClick,
    onOpenMenu,
    onUpdate,
    onCloseMenu,
    onHover,
}) => {
    const framework = useFramework();
    const { events, registration } = framework.service;
    const pluginInstance = registration.getInstance<StageToolbar>('StageToolbar');
    const { menuItems: menus, preventHiddenTypes } = pluginInstance.getHierarchyConfig();
    const availableMenus = menus.filter(({ predicate }) => (predicate ? predicate(layer) : true));

    const {
        // width: layerWidth,
        // height: layerHeight,
        // left: layerLeft,
        // top: layerTop,
        layerType,
        children,
        lock,
        hidden,
    } = layer;
    const isGroup = layerType === LayerType.GROUP_LAYER;
    const isBG = layerType === LayerType.BACKGROUND_LAYER;
    const isMain = layerType === LayerType.MAIN_LAYER;
    const isStatic = isBG || isMain;
    const isDragDisabled = isStatic || ancestorLayerLocked || lock;
    const childrenLen = isGroup ? children.length : 0;

    const behavior = framework.service.behavior.get(layer)!;
    const title = behavior.getMenuOverlapName(layer); //getLayerTitle(layer);

    const handleClick = () => {
        if (active && framework.service.edgelessCanvas.isEdgelessMode) {
            events.emit(HIERARCHY_EVENTS.SET_OBJECT_TO_CENTER, layer);
        }
        if (active && !isGroup) return;
        onClick();
    };

    const handleHover = (flag: boolean) => {
        if (hidden) return;
        onHover?.(flag);
    };

    const handleOpenMenu = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (onOpenMenu) onOpenMenu();
    };

    const handleUnlock = (event: React.MouseEvent) => {
        event.stopPropagation();
        events.emit(HIERARCHY_EVENTS.LAYER_OPERATION, layer, 'unlock');
    };

    const handleCloseMenu = () => {
        if (onCloseMenu) onCloseMenu();
    };

    const handleOperate = (item: HierarchyMenuItem) => {
        events.emit(HIERARCHY_EVENTS.LAYER_OPERATION, layer, item.operation);
    };

    useEffect(() => {
        const listener = framework.service.flatLayer.onChange(() => {
            onUpdate();
        }, layer.id);

        return () => {
            events.off(listener);
        };
    }, []);

    const renderActions = () => {
        if (isStatic || ancestorLayerLocked) return null;

        if (lock) {
            return (
                <div className="hierarchy-item__actions">
                    <span className="hierarchy-item__lock" onClick={handleUnlock}>
                        <IconFont name="LockBold" fontSize={12} />
                    </span>
                </div>
            );
        }

        if (availableMenus.length === 0) return null;

        return (
            <div className="hierarchy-item__actions">
                <Popover
                    trigger="click"
                    open={menuOpen}
                    showArrow={false}
                    placement="bottom"
                    overlayClassName="hierarchy-item__menu"
                    content={<Menu menus={menus} onOperate={handleOperate} onClickAway={handleCloseMenu} />}
                >
                    <IconFont name="EllipsisBold" fontSize={12} onClick={handleOpenMenu} />
                </Popover>
            </div>
        );
    };

    const handleHidden = (hidden: boolean, event: React.MouseEvent) => {
        event.stopPropagation();
        events.emit(HIERARCHY_EVENTS.LAYER_OPERATION, layer, hidden ? 'hide' : 'show');
    };

    const renderItem = (extraProps: Record<string, unknown> = {}, extraStyles: React.CSSProperties = {}) => {
        const { isDragging, ...restDOMProps } = extraProps;

        return (
            <div
                {...restDOMProps}
                className={classNames(
                    'hierarchy-item',
                    {
                        'hierarchy-item--grouped': isGroup,
                        'hierarchy-item--active': active,
                        'hierarchy-item--locked': lock,
                        'hierarchy-item--static': isStatic,
                        'hierarchy-item--dragging': isDragging,
                        'hierarchy-item--hidden': hidden,
                    },
                    className,
                )}
                style={{
                    ...extraStyles,
                    ...style,
                }}
                onClick={handleClick}
                onMouseEnter={() => handleHover(true)}
                onMouseLeave={() => handleHover(false)}
            >
                <div className="hierarchy-item__content">
                    <div className="hierarchy-item__info">
                        {/* 拖拽图标 */}
                        {!isStatic ? (
                            <div className="hierarchy-item__drag">
                                {!lock && !ancestorLayerLocked ? <IconFont name="Drag" fontSize={12} /> : null}
                            </div>
                        ) : null}

                        {/* 图层缩略图 */}
                        <HierarchyThumbnail behavior={behavior} layer={layer} iconRenderVersion={iconRenderVersion} />

                        {/* 图层名称 */}
                        <div className="hierarchy-item__name">{title}</div>

                        {/* 隐藏 */}
                        {!preventHiddenTypes.includes(layer.layerType) && (
                            <div className="hierarchy-item__hidden" onClick={(e) => handleHidden(!layer.hidden, e)}>
                                {layer.hidden ? (
                                    <IconFont name="InvisibleBold" fontSize={12} />
                                ) : (
                                    <IconFont name="VisibleBold" fontSize={12} />
                                )}
                            </div>
                        )}
                    </div>

                    {/* 菜单 */}
                    {renderActions()}
                </div>

                {/* 组图层角标：显示子图层数量 */}
                {childrenLen > 0 ? (
                    <div className="hierarchy-item__num">
                        <span>{childrenLen}</span>
                    </div>
                ) : null}

                {/* 组图层底部装饰 */}
                {isGroup ? (
                    <div className="hierarchy-item__bottom">
                        <svg xmlns="http://www.w3.org/2000/svg" width="184" height="4" viewBox="0 0 184 4">
                            <path d="M184.002 0C183.178 2.33011 180.957 4 178.345 4H5.65723C3.04498 4 0.823821 2.33011 0 0H184.002Z" />
                        </svg>
                    </div>
                ) : null}
            </div>
        );
    };

    if (isBG || isMain) return renderItem();

    return (
        <Draggable draggableId={layer.id} index={index} isDragDisabled={isDragDisabled}>
            {(provided, snapshot) =>
                renderItem(
                    {
                        ref: provided.innerRef,
                        ...provided.draggableProps,
                        ...provided.dragHandleProps,
                        isDragging: snapshot.isDragging,
                    },
                    provided.draggableProps.style,
                )
            }
        </Draggable>
    );
};
